import React, { useState } from 'react';
import {
  CalendarDaysIcon,
  BuildingStorefrontIcon,
  BanknotesIcon,
  ClipboardDocumentCheckIcon,
  ChatBubbleBottomCenterTextIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

/**
 * Step 3 of the Qualifying Agent flow: what the agent may say when the candidate asks it
 * something. Candidates on a screening call ask the same handful of things — weekly off, shift
 * timings, dress code, travel, when salary comes — and an agent that guesses an answer makes a
 * promise the company never made. So the agent answers only from what the recruiter writes here,
 * and anything else gets the fallback reply, word for word.
 *
 * Suggestions are a starting list, not saved questions: a suggestion becomes an FAQ only when the
 * recruiter writes its answer. Unanswered rows are dropped on save (cleanFaqs).
 *
 * Props:
 *  faqs              [{ id, question, answer }]
 *  onChange          setter for the whole array
 *  fallback          the reply for anything not covered
 *  onFallbackChange  setter for it
 */

export const DEFAULT_FALLBACK_REPLY =
  "That's a good question. I don't have that detail with me right now — the hiring team will be able to answer it when they get in touch with you.";

// The questions candidates ask most on hourly / frontline screening calls (retail, QSR, BPO,
// field sales, warehouse) — grouped the way a recruiter thinks about the job.
const SUGGESTIONS = [
  {
    group: 'Schedule',
    Icon: CalendarDaysIcon,
    items: [
      { question: 'What are the working days?', placeholder: 'e.g. 6 days a week, Monday to Saturday' },
      { question: 'Is it full-time or part-time?', placeholder: 'e.g. Full-time, about 48 hours a week' },
      { question: 'Is Sunday or the weekend off?', placeholder: 'e.g. One weekly off on a rotational weekday — weekends are working days in stores' },
      { question: 'What are the shift timings?', placeholder: 'e.g. 9-hour shifts between 10 AM and 10 PM' },
      { question: 'Are there night or rotational shifts?', placeholder: 'e.g. Rotational shifts, no night shifts' },
      { question: 'How many hours a day is the job?', placeholder: 'e.g. 9 hours including a 1-hour break' },
    ],
  },
  {
    group: 'Workplace',
    Icon: BuildingStorefrontIcon,
    items: [
      { question: 'Is there a dress code or uniform?', placeholder: 'e.g. Company uniform provided; black formal shoes' },
      { question: 'Will I need to travel for this job?', placeholder: 'e.g. No travel — you work at one store' },
      { question: 'Can I choose my work location?', placeholder: 'e.g. We try to place you at a store near your home' },
      { question: 'Is work from home possible?', placeholder: 'e.g. No, this is an on-site role' },
      { question: 'Do I need my own vehicle or a licence?', placeholder: 'e.g. Not required' },
      { question: 'Is accommodation provided?', placeholder: 'e.g. No, but we help outstation joiners find a PG' },
    ],
  },
  {
    group: 'Pay & benefits',
    Icon: BanknotesIcon,
    items: [
      { question: 'What is the in-hand salary?', placeholder: 'e.g. About ₹18,500 a month after PF and ESI' },
      { question: 'When is the salary paid each month?', placeholder: 'e.g. By the 7th of every month, to your bank account' },
      { question: 'Are there incentives on top of salary?', placeholder: 'e.g. Monthly sales incentives on top of fixed pay' },
      { question: 'Is PF and ESI provided?', placeholder: 'e.g. Yes, PF and ESI as per law' },
      { question: 'Is food or transport provided?', placeholder: 'e.g. One meal per shift; no transport' },
      { question: 'Is overtime paid?', placeholder: 'e.g. Yes, paid at the hourly rate' },
      { question: 'Is health insurance provided?', placeholder: 'e.g. Yes, from your first day, for you and your family' },
      { question: 'Is there extra pay for night shifts?', placeholder: 'e.g. Yes, a night allowance for shifts after 10 PM' },
    ],
  },
  {
    group: 'Joining',
    Icon: ClipboardDocumentCheckIcon,
    items: [
      // Fake "registration fee" job offers are common, so candidates ask; the answer should be no.
      { question: 'Is there any fee or deposit to join?', placeholder: 'e.g. No. We never charge candidates any fee.' },
      { question: 'What are the next steps after this call?', placeholder: 'e.g. A short AI interview, then a store visit' },
      { question: 'Which documents do I need to join?', placeholder: 'e.g. Aadhaar, PAN, bank details and last education certificate' },
      { question: 'Is there training before I start?', placeholder: 'e.g. 7 days of paid training' },
      { question: 'Is this a permanent job or contract?', placeholder: 'e.g. Full-time, on company payroll' },
      { question: 'Is there a probation period?', placeholder: 'e.g. 3 months' },
      { question: 'Is there a background check?', placeholder: 'e.g. Yes, a standard address and ID check before joining' },
    ],
  },
];

const newId = () => `faq_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

/** FAQs worth saving: both halves filled, trimmed. */
export function cleanFaqs(faqs = []) {
  return faqs
    .map((f) => ({ question: String(f.question || '').trim(), answer: String(f.answer || '').trim() }))
    .filter((f) => f.question && f.answer);
}

export default function QualifyingAgentFaqStep({ faqs = [], onChange, fallback, onFallbackChange }) {
  const [openGroup, setOpenGroup] = useState(SUGGESTIONS[0].group);
  const asked = new Set(faqs.map((f) => f.question.trim().toLowerCase()));
  const answered = cleanFaqs(faqs).length;

  const update = (id, patch) => onChange(faqs.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const remove = (id) => onChange(faqs.filter((f) => f.id !== id));
  const add = (question = '', placeholder = '') =>
    onChange([...faqs, { id: newId(), question, answer: '', placeholder }]);

  return (
    <div className="w-full space-y-5">
      <div className="flex items-start gap-3 rounded-xl border border-[#007BFF]/15 bg-gradient-to-br from-[#007BFF]/5 to-transparent p-4">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#007BFF]/10 text-[#007BFF]">
          <ShieldCheckIcon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">When the candidate asks the agent something</h3>
          <p className="mt-0.5 text-xs leading-relaxed text-gray-600">
            On the call, the agent answers only from what you write here — it never guesses.
            Anything else gets the reply at the bottom.
          </p>
        </div>
      </div>

      {/* ── Suggestions ─────────────────────────────────────────────────── */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500">Questions candidates often ask</h4>
          <span className="text-[11px] text-gray-400">Tap one to answer it</span>
        </div>
        <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
          {SUGGESTIONS.map(({ group, Icon, items }) => {
            const open = openGroup === group;
            const used = items.filter((i) => asked.has(i.question.toLowerCase())).length;
            return (
              <div key={group}>
                <button
                  type="button"
                  onClick={() => setOpenGroup(open ? null : group)}
                  aria-expanded={open}
                  className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left transition-colors hover:bg-gray-50"
                >
                  <Icon className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="flex-1 text-sm font-semibold text-gray-800">{group}</span>
                  {used > 0 && (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                      {used} added
                    </span>
                  )}
                  <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && (
                  <div className="flex flex-wrap gap-2 px-3.5 pb-3.5">
                    {items.map((item) => {
                      const isAdded = asked.has(item.question.toLowerCase());
                      return (
                        <button
                          key={item.question}
                          type="button"
                          disabled={isAdded}
                          data-testid="qa-faq-suggestion"
                          onClick={() => add(item.question, item.placeholder)}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                            isAdded
                              ? 'cursor-default border-emerald-200 bg-emerald-50 text-emerald-700'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-[#007BFF]/50 hover:bg-[#007BFF]/5 hover:text-[#007BFF]'
                          }`}
                        >
                          {isAdded ? <CheckCircleIcon className="h-3.5 w-3.5" /> : <PlusIcon className="h-3.5 w-3.5" />}
                          {item.question}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── The recruiter's answers ─────────────────────────────────────── */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500">
            Your answers <span className="font-medium normal-case tracking-normal text-gray-400">· {answered} ready</span>
          </h4>
          <button
            type="button"
            onClick={() => add()}
            data-testid="qa-faq-add"
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:border-[#007BFF]/50 hover:bg-[#007BFF]/5 hover:text-[#007BFF]"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Write your own
          </button>
        </div>

        {faqs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center">
            <p className="text-sm font-medium text-gray-700">No answers yet</p>
            <p className="mt-1 text-xs text-gray-500">
              Optional — without any, every question gets the fallback reply.
            </p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {faqs.map((f) => {
              const missing = f.question.trim() && !f.answer.trim();
              return (
                <div
                  key={f.id}
                  data-testid="qa-faq-card"
                  className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition-colors focus-within:border-[#007BFF]/40"
                >
                  <div className="flex items-center gap-2">
                    <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold text-gray-500">Q</span>
                    <input
                      type="text"
                      value={f.question}
                      onChange={(e) => update(f.id, { question: e.target.value })}
                      placeholder="What the candidate might ask"
                      aria-label="Candidate question"
                      className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm font-semibold text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
                    />
                    <button
                      type="button"
                      onClick={() => remove(f.id)}
                      aria-label="Remove this question"
                      title="Remove"
                      className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-2 flex items-start gap-2">
                    <span className="mt-2 rounded-md bg-[#007BFF]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#007BFF]">A</span>
                    <textarea
                      value={f.answer}
                      onChange={(e) => update(f.id, { answer: e.target.value })}
                      rows={2}
                      placeholder={f.placeholder || 'The answer the agent gives'}
                      aria-label="Answer"
                      className={`w-full resize-y rounded-lg border px-3 py-2 text-sm text-gray-900 placeholder-gray-400 transition-all focus:outline-none focus:ring-2 ${
                        missing
                          ? 'border-amber-300 focus:border-amber-400 focus:ring-amber-100'
                          : 'border-gray-200 focus:border-[#007BFF] focus:ring-[#007BFF]/20'
                      }`}
                    />
                  </div>
                  {missing && (
                    <p className="ml-7 mt-1 text-[11px] text-amber-600">No answer yet — this one is skipped until you add it.</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Fallback ────────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
        <div className="flex items-center gap-2">
          <ChatBubbleBottomCenterTextIcon className="h-4 w-4 text-gray-500" />
          <h4 className="text-sm font-semibold text-gray-900">For anything else</h4>
        </div>
        <p className="mt-0.5 text-xs text-gray-500">
          Leave policy, paid leave, appraisal or anything not answered above — the agent says this and
          carries on with the call.
        </p>
        <textarea
          value={fallback}
          onChange={(e) => onFallbackChange(e.target.value)}
          rows={2}
          data-testid="qa-faq-fallback"
          className="mt-2.5 w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 transition-all focus:border-[#007BFF] focus:outline-none focus:ring-2 focus:ring-[#007BFF]/20"
        />
        {fallback.trim() !== DEFAULT_FALLBACK_REPLY && (
          <button
            type="button"
            onClick={() => onFallbackChange(DEFAULT_FALLBACK_REPLY)}
            className="mt-1 text-xs font-medium text-[#007BFF] hover:underline"
          >
            Reset to the suggested reply
          </button>
        )}
      </section>
    </div>
  );
}
